<template>
    <div class="mb-8 mt-4 h-full px-4 py-3 md:px-6 xl:mt-0">
        <checkout-form-kiosk-guest-balance-due-mode
            v-if="primaryCustomer === null && balanceDueMode"
            :query="query"
            :checkout="checkout"
            :contract="contract"
            @checkout-status-changed="$emit('checkoutStatusChanged')"
        />

        <checkout-form-kiosk-guest
            v-else-if="primaryCustomer === null"
            :query="query"
            :checkout="checkout"
            :contract="contract"
            @kiosk-checkout-success="$emit('kioskCheckoutSuccess', $event)"
            @checkout-status-changed="$emit('checkoutStatusChanged')"
        />

        <checkout-form-online-customer
            v-else-if="!isKiosk"
            :query="query"
            :checkout="checkout"
            :contract="contract"
            @checkout-success="$emit('checkoutSuccess', $event)"
            @checkout-status-changed="$emit('checkoutStatusChanged')"
        />

        <checkout-form-kiosk-customer-balance-due-mode
            v-else-if="balanceDueMode"
            :query="query"
            :checkout="checkout"
            :contract="contract"
            @checkout-status-changed="$emit('checkoutStatusChanged')"
        />

        <checkout-form-kiosk-customer
            v-else
            :query="query"
            :checkout="checkout"
            :contract="contract"
            @kiosk-checkout-success="$emit('kioskCheckoutSuccess', $event)"
            @checkout-status-changed="$emit('checkoutStatusChanged')"
        />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { CheckoutSuccessFragment } from '~/components/Checkout/CheckoutSuccess.vue'
import { CheckoutSuccessKioskDisplayFragment } from '~/components/Checkout/CheckoutSuccessKioskDisplay.vue'
import { type FragmentType, getFragmentData } from '~/resources/graphql'
import { DocumentSectionContractFragment } from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import { useLayoutMode } from '~/composables/use-layout-mode'
import {
    CheckoutFormCheckoutFragment,
    CheckoutFormQueryFragment
} from '~/components/Checkout/Forms/use-checkout-form'
import CheckoutFormKioskGuest from '~/components/Checkout/Forms/CheckoutFormKioskGuest.vue'
import CheckoutFormOnlineCustomer from '~/components/Checkout/Forms/CheckoutFormOnlineCustomer.vue'
import CheckoutFormKioskCustomerBalanceDueMode from '~/components/Checkout/Forms/CheckoutFormKioskCustomerBalanceDueMode.vue'
import CheckoutFormKioskCustomer from '~/components/Checkout/Forms/CheckoutFormKioskCustomer.vue'
import CheckoutFormKioskGuestBalanceDueMode from '~/components/Checkout/Forms/CheckoutFormKioskGuestBalanceDueMode.vue'

const props = withDefaults(
    defineProps<{
        query: FragmentType<typeof CheckoutFormQueryFragment>
        checkout: FragmentType<typeof CheckoutFormCheckoutFragment>
        contract?: FragmentType<typeof DocumentSectionContractFragment>
        balanceDueMode?: boolean
    }>(),
    { balanceDueMode: false }
)

defineEmits<{
    (
        e: 'checkoutSuccess',
        payload: FragmentType<typeof CheckoutSuccessFragment>
    ): void
    (e: 'checkoutStatusChanged'): void
    (
        e: 'kioskCheckoutSuccess',
        payload: FragmentType<typeof CheckoutSuccessKioskDisplayFragment>
    ): void
}>()

const queryData = computed(() => {
    return getFragmentData(CheckoutFormQueryFragment, props.query)
})

const primaryCustomer = computed(() => {
    if (queryData.value.viewer === null) {
        return null
    }

    return queryData.value.viewer.primaryCustomer
})

const { isKiosk } = useLayoutMode()
</script>
